/**
 * Animation System - Scroll-Triggered Animations
 *
 * Automatically triggers animation classes when elements come into view
 * using IntersectionObserver. Works with existing animation classes.
 *
 * Below 992px (theme laptop breakpoint), the observer is skipped; CSS shows
 * elements immediately (see _animations.scss).
 *
 * @package SOJ_Core_Modern
 * @since 2.0.0
 */

const CSS_OBSERVER_MOTION_MIN_WIDTH = 992;

function isCssScrollAnimationViewport() {
    return typeof window.matchMedia === 'function'
        && window.matchMedia(`(min-width: ${CSS_OBSERVER_MOTION_MIN_WIDTH}px)`).matches;
}

class AnimationSystem {
    constructor() {
        this.observer = null;
        this.animatedElements = new Set();
        this.init();
    }

    isDebugEnabled() {
        return typeof sojTheme !== 'undefined' && sojTheme.debug;
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupObserver());
        } else {
            this.setupObserver();
        }
    }

    setupObserver() {
        if (!isCssScrollAnimationViewport()) {
            if (this.isDebugEnabled()) {
                console.log('[SOJ] CSS scroll animations skipped (viewport < ' + CSS_OBSERVER_MOTION_MIN_WIDTH + 'px)');
            }
            return;
        }

        // Create intersection observer
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.triggerAnimation(entry.target);
                        // Stop observing once animation is triggered
                        this.observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.5, // Trigger when 20% of element is visible
                rootMargin: '0px 0px 10% 0px' // Start animation slightly before element enters viewport
            }
        );

        // Find and observe all elements with animation classes
        this.observeAnimationElements();

        if (this.isDebugEnabled()) {
            console.log('[SOJ] Animation system initialized');
        }
    }

    observeAnimationElements() {
        // Find all elements with the base 'animate' class
        const elements = document.querySelectorAll('.animate');
        
        elements.forEach(element => {
            // Only observe elements that haven't been animated yet
            if (!this.animatedElements.has(element)) {
                this.observer.observe(element);
            }
        });
    }

    triggerAnimation(element) {
        // Mark element as animated
        this.animatedElements.add(element);
        
        // Add 'in-view' class to trigger the animation
        element.classList.add('in-view');
    }

    // Method to manually observe new elements (useful for dynamically added content)
    observeElement(element) {
        if (this.observer && !this.animatedElements.has(element)) {
            this.observer.observe(element);
        }
    }

    // Method to observe all animation elements again (useful for dynamic content)
    refresh() {
        this.observeAnimationElements();
    }
}

// Export the class
export default AnimationSystem;
